import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, MapPin } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Props {
  userId: string;
}

interface LoadCard {
  id: string;
  origin: string;
  destination: string;
  equipment: string | null;
  bidCount?: number;
  postedAt?: string;
  brokerLabel?: string;
  status?: string;
}

type Column = {
  key: "posted" | "awarded" | "in_transit" | "delivered";
  label: string;
  rows: LoadCard[];
};

const COLUMN_DEFS: { key: Column["key"]; label: string; dispatchStatuses?: string[] }[] = [
  { key: "posted", label: "Posted" },
  { key: "awarded", label: "Awarded", dispatchStatuses: ["assigned", "ready"] },
  { key: "in_transit", label: "In Transit", dispatchStatuses: ["in_transit", "picked_up", "en_route"] },
  { key: "delivered", label: "Delivered / Docs", dispatchStatuses: ["delivered", "completed"] },
];

function formatLane(o: string, oS: string, d: string, dS: string) {
  return `${o || "—"}, ${oS || "?"} → ${d || "—"}, ${dS || "?"}`;
}

function timeAgo(iso?: string) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Section 3 — Active Loads Board.
 * Groups loads by lifecycle stage (load.status for posted, dispatch.status thereafter).
 */
export function ActiveLoadsBoard({ userId }: Props) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["shipper-active-loads", userId],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: loads } = await supabase
        .from("direct_loads")
        .select(
          "id, status, equipment_type, posted_at, created_at, origin_city, origin_state, destination_city, destination_state",
        )
        .eq("created_by", userId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(80);

      const loadList = loads || [];
      const loadIds = loadList.map((l: any) => l.id);
      const loadById = new Map(loadList.map((l: any) => [l.id, l]));

      const { data: bids } = loadIds.length
        ? await supabase
            .from("direct_bids")
            .select("direct_load_id, status")
            .in("direct_load_id", loadIds)
        : { data: [] as any[] };
      const bidCounts = new Map<string, number>();
      (bids || []).forEach((b: any) => {
        bidCounts.set(b.direct_load_id, (bidCounts.get(b.direct_load_id) || 0) + 1);
      });

      const { data: dispatches } = loadIds.length
        ? await supabase
            .from("direct_dispatches")
            .select("id, direct_load_id, awarded_broker_id, status, updated_at")
            .in("direct_load_id", loadIds)
            .order("updated_at", { ascending: false })
        : { data: [] as any[] };

      const brokerIds = Array.from(
        new Set((dispatches || []).map((d: any) => d.awarded_broker_id).filter(Boolean)),
      );
      const { data: brokers } = brokerIds.length
        ? await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .in("id", brokerIds)
        : { data: [] as any[] };
      const brokerLabel = new Map(
        (brokers || []).map((b: any) => [
          b.id,
          [b.first_name, b.last_name].filter(Boolean).join(" ") || b.email || "Broker",
        ]),
      );

      const columns: Record<Column["key"], LoadCard[]> = {
        posted: [],
        awarded: [],
        in_transit: [],
        delivered: [],
      };

      loadList
        .filter((l: any) => l.status === "posted")
        .slice(0, 5)
        .forEach((l: any) => {
          columns.posted.push({
            id: l.id,
            origin: l.origin_city,
            destination: l.destination_city,
            equipment: l.equipment_type,
            bidCount: bidCounts.get(l.id) || 0,
            postedAt: l.posted_at || l.created_at,
          });
        });

      (dispatches || []).forEach((d: any) => {
        const colDef = COLUMN_DEFS.find((c) => c.dispatchStatuses?.includes(d.status));
        if (!colDef) return;
        if (columns[colDef.key].length >= 5) return;
        const l: any = loadById.get(d.direct_load_id);
        if (!l) return;
        columns[colDef.key].push({
          id: l.id,
          origin: l.origin_city,
          destination: l.destination_city,
          equipment: l.equipment_type,
          brokerLabel: brokerLabel.get(d.awarded_broker_id) || "Awarded broker",
          status: d.status,
        });
      });

      return COLUMN_DEFS.map((c) => ({ key: c.key, label: c.label, rows: columns[c.key] })) as Column[];
    },
  });

  const columns = data || COLUMN_DEFS.map((c) => ({ key: c.key, label: c.label, rows: [] as LoadCard[] }));

  const renderRow = (row: LoadCard, col: Column["key"]) => (
    <li
      key={row.id + col}
      className="cursor-pointer rounded-md border bg-background p-2.5 text-xs hover:border-brand-teal-deep"
      onClick={() => navigate(col === "posted" ? `/lr-direct/my-loads` : `/lr-direct/dispatches`)}
    >
      <div className="flex items-start gap-1.5 font-semibold text-foreground">
        <MapPin className="mt-[2px] h-3 w-3 flex-shrink-0 text-muted-foreground" />
        <span className="truncate">{formatLane(row.origin, "", row.destination, "")}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="truncate">{row.equipment || "—"}</span>
        <span className="ml-2 flex-shrink-0">
          {col === "posted"
            ? `${row.bidCount ?? 0} bid${row.bidCount === 1 ? "" : "s"} · ${timeAgo(row.postedAt)}`
            : row.brokerLabel}
        </span>
      </div>
    </li>
  );

  const renderColumn = (col: Column) => (
    <div key={col.key} className="flex min-h-[120px] flex-col gap-2">
      <div className="flex items-center justify-between">
        <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
          {col.label}
        </h4>
        <span className="text-[11px] text-muted-foreground">{col.rows.length}</span>
      </div>
      {col.rows.length === 0 ? (
        <div className="rounded-md border border-dashed p-3 text-center text-[11px] text-muted-foreground">
          None
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">{col.rows.map((r) => renderRow(r, col.key))}</ul>
      )}
    </div>
  );

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground">Active Loads</h3>
        <button
          className="group inline-flex items-center gap-1 text-xs font-semibold text-brand-teal-deep hover:underline"
          onClick={() => navigate("/lr-direct/my-loads")}
        >
          View All <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-md bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Desktop / tablet: 4 columns at xl, 2 at md */}
          <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-4 gap-3">
            {columns.map(renderColumn)}
          </div>
          {/* Mobile: tabs */}
          <div className="md:hidden">
            <Tabs defaultValue="posted">
              <TabsList className="grid w-full grid-cols-4">
                {columns.map((c) => (
                  <TabsTrigger key={c.key} value={c.key} className="text-[10px]">
                    {c.label.split(" ")[0]} ({c.rows.length})
                  </TabsTrigger>
                ))}
              </TabsList>
              {columns.map((c) => (
                <TabsContent key={c.key} value={c.key} className="mt-3">
                  {renderColumn(c)}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </>
      )}
    </section>
  );
}
